import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useCanvasStore } from '@/stores/canvasStore';
import { toast } from 'sonner';
import { compressImage, formatBytes } from '@/lib/imageCompression';
import { normalizeImageFile } from '@/lib/tiffImage';
import { generateVideoThumbnail, getVideoMetadata, VIDEO_TYPES, calculateVideoDisplaySize } from '@/lib/videoProcessing';
import { markdownFileToDocument, isMarkdownFile } from '@/lib/markdownUtils';
import { isExtractableFile, requestFileExtraction } from '@/lib/fileExtraction';
import { checkStorageLimit, updateStorageUsage } from '@/lib/storageUsage';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';
import { useFileDragOver } from '@/hooks/useFileDragOver';
import { isFocusInTextOrOverlay } from '@/lib/canvasEventGuards';
import { UPLOAD_REQUEST_EVENT, type UploadRequest } from '@/lib/uploadRequests';
import { DropVeil, UploadTray, type UploadKind, type UploadTrayState } from './UploadSurfaces';

interface DropZoneProps {
  children: React.ReactNode;
  mode: 'canvas' | 'wall';
  onFilesUploaded?: (items: Array<{ type: 'image' | 'file' | 'video'; url: string; filename: string; mimeType: string; size: number; storagePath: string }>) => void;
  /** Canvas position for a point on screen (a drop, or the middle of the view for a paste) */
  getDropPosition?: (at: { clientX: number; clientY: number }) => { x: number; y: number };
}

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const calculateDisplaySize = (
  naturalWidth: number, 
  naturalHeight: number,
  maxWidth: number = 400,
  maxHeight: number = 400
) => {
  const aspectRatio = naturalWidth / naturalHeight;
  
  let displayWidth = naturalWidth;
  let displayHeight = naturalHeight;
  
  // Scale down if too wide
  if (displayWidth > maxWidth) {
    displayWidth = maxWidth;
    displayHeight = displayWidth / aspectRatio;
  }
  
  // Scale down if still too tall
  if (displayHeight > maxHeight) {
    displayHeight = maxHeight;
    displayWidth = displayHeight * aspectRatio;
  }
  
  return { 
    width: Math.round(displayWidth), 
    height: Math.round(displayHeight),
    aspectRatio 
  };
};

// AI image analysis function
const analyzeImageWithAI = async (imageUrl: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('analyze-image', {
      body: { imageUrl }
    });

    if (error) {
      console.error('Image analysis error:', error);
      return null;
    }

    return data?.analysis || null;
  } catch (e) {
    console.error('Failed to analyze image:', e);
    return null;
  }
};

type UploadState = UploadTrayState;

function uploadKind(file: File): UploadKind {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type === 'application/pdf') return 'pdf';
  return 'file';
}

export function DropZone({ children, mode, onFilesUploaded, getDropPosition }: DropZoneProps) {
  // Clears itself when a drag ends anywhere, so the overlay can't stay on
  const { over: isDragOver, touch: touchDragOver, clear: clearDragOver } = useFileDragOver();
  const [uploadState, setUploadState] = useState<UploadState | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  // How many files the drag carries (browsers say how many, not what, until the drop)
  const [dragCount, setDragCount] = useState(0);
  // Each upload run, so a finished run's timer never closes the tray on the next one
  const runId = useRef(0);
  const { createItem, currentBoardId, boards } = useCanvasStore();
  // Width the docked Partnr panel covers on the right (0 when closed)
  const partnrInset = usePartnrPanelStore((s) => s.rightInset);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types.includes('Files')) return;
    setDragCount(Array.from(e.dataTransfer.items ?? []).filter((i) => i.kind === 'file').length);
    touchDragOver();
  }, [touchDragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    // Leaving the window reports 0,0, which is inside a zone that starts at the corner
    const leftWindow = x === 0 && y === 0;
    if (leftWindow || x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      clearDragOver();
    }
  }, [clearDragOver]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // A drag that keeps hovering keeps the overlay; when these stop, it clears
    if (e.dataTransfer.types.includes('Files')) touchDragOver();
  }, [touchDragOver]);

  const uploadFile = async (file: File, onProgress?: (progress: number) => void): Promise<{ url: string; filename: string; mimeType: string; size: number; storagePath: string } | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in to upload files.');
      return null;
    }

    let fileToUpload = file;
    const isImage = IMAGE_TYPES.includes(file.type);
    
    // Compress images first to get actual file size
    if (isImage) {
      onProgress?.(10);
      const result = await compressImage(file);
      fileToUpload = result.file;
      // Compression info is shown via uploadState, not toast
      onProgress?.(20);
    }

    // Check storage limit before uploading
    const fileSizeMB = fileToUpload.size / (1024 * 1024);
    const canUpload = await checkStorageLimit(user.id, fileSizeMB);
    if (!canUpload) {
      return null;
    }

    onProgress?.(30);

    const fileExt = file.name.split('.').pop();
    // User-scoped file path for RLS compliance
    const storagePath = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    onProgress?.(50);

    const { error: uploadError } = await supabase.storage
      .from('uploads')
      .upload(storagePath, fileToUpload);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      toast.error(`Failed to upload ${file.name}. Please try again.`);
      return null;
    }

    onProgress?.(80);

    // Update storage usage after successful upload
    await updateStorageUsage(user.id, fileSizeMB);

    onProgress?.(90);

    // Use signed URL instead of public URL for private bucket
    const { data: signedUrlData, error: urlError } = await supabase.storage
      .from('uploads')
      .createSignedUrl(storagePath, 31536000); // 1 year expiry

    if (urlError || !signedUrlData) {
      console.error('Failed to get signed URL:', urlError);
      toast.error(`Failed to get URL for ${file.name}.`);
      return null;
    }

    onProgress?.(100);

    return {
      url: signedUrlData.signedUrl,
      filename: file.name,
      mimeType: file.type,
      size: fileToUpload.size,
      storagePath,
    };
  };

  /** Uploads files dropped or pasted at a point on screen, one after another, cascading from it. */
  const runUpload = useCallback(async (files: File[], at: { clientX: number; clientY: number }) => {
    if (files.length === 0) return;

    // Get target board
    let targetBoardId = currentBoardId;
    if (mode === 'wall' && !targetBoardId) {
      const homeBoard = boards.find(b => b.parent_id === null);
      targetBoardId = homeBoard?.id || null;
    }

    if (!targetBoardId) {
      toast.error('No board selected. Please navigate to a board first.');
      return;
    }

    const run = ++runId.current;
    const previews = files.slice(0, 3).map((f) => ({ name: f.name, kind: uploadKind(f), url: f.type.startsWith('image/') ? URL.createObjectURL(f) : null }));
    setShowSuccess(false);
    setUploadState({ total: files.length, completed: 0, current: files[0].name, progress: 0, stage: 'uploading', previews });

    const dropPosition = getDropPosition ? getDropPosition(at) : { x: 100, y: 100 };
    const uploadedItems: Array<{ type: 'image' | 'file' | 'video'; url: string; filename: string; mimeType: string; size: number; storagePath: string }> = [];
    let offsetX = 0;
    let offsetY = 0;

    for (let i = 0; i < files.length; i++) {
      // TIFF -> PNG so it lands as an image card
      const file = await normalizeImageFile(files[i]);
      setUploadState(prev => prev ? { ...prev, current: file.name, completed: i, progress: 0, stage: 'uploading' } : prev);
      
      // Handle markdown files - convert to text card or document based on length
      if (isMarkdownFile(file.name)) {
        try {
          const markdownText = await file.text();
          const doc = markdownFileToDocument(markdownText, file.name);

          // Use document for longer content (>500 chars), text card for shorter
          const isLongContent = markdownText.length > 500;

          await createItem({
            board_id: targetBoardId,
            item_type: isLongContent ? 'document' : 'text_card',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            // Long files land as a page-shaped card that previews the document
            width: 320,
            height: isLongContent ? 400 : 240,
            content: isLongContent
              ? { body: doc.body, title: doc.title, wordCount: doc.wordCount, source: 'markdown' }
              : { body: doc.body, title: doc.title },
          });
          
          offsetX += 20;
          offsetY += 20;
        } catch (err) {
          console.error('Failed to read markdown file:', err);
          toast.error(`Failed to process ${file.name}`);
        }
        continue;
      }
      
      const result = await uploadFile(file, (progress) => {
        setUploadState(prev => prev ? { ...prev, progress } : prev);
      });
      
      if (result) {
        const isImage = IMAGE_TYPES.includes(file.type);
        const isVideo = VIDEO_TYPES.includes(file.type);
        
        uploadedItems.push({ 
          ...result, 
          type: isImage ? 'image' : isVideo ? 'video' : 'file' 
        });

        let width = 280;
        let height = 100;
        let itemContent: Record<string, unknown> = {};

        if (isImage) {
          try {
            const dims = await getImageDimensions(file);
            const size = calculateDisplaySize(dims.width, dims.height);
            width = size.width;
            height = size.height;
            itemContent = { 
              naturalWidth: dims.width, 
              naturalHeight: dims.height, 
              aspectRatio: size.aspectRatio 
            };
          } catch (e) {
            console.warn('Could not get image dimensions, using defaults');
            width = 400;
            height = 300;
          }

          // Analyze image with AI (non-blocking - start analysis)
          setUploadState(prev => prev ? { ...prev, stage: 'analyzing', stageText: 'Analyzing with AI' } : prev);
          
          // Start AI analysis - it will be added to the item asynchronously
          const analysisPromise = analyzeImageWithAI(result.url);
          
          // Create item first so user sees it immediately
          const createdItem = await createItem({
            board_id: targetBoardId,
            item_type: 'image',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            width,
            height,
            content: { 
              url: result.url, 
              alt: result.filename, 
              storagePath: result.storagePath,
              mimeType: file.type,
              ...itemContent 
            },
          });

          // Wait for analysis and update item
          const analysis = await analysisPromise;
          if (analysis && createdItem) {
            // Update item with AI analysis for search
            const searchText = `${result.filename} ${analysis}`.toLowerCase();
            await supabase
              .from('items')
              .update({ 
                content: { 
                  url: result.url, 
                  alt: result.filename, 
                  storagePath: result.storagePath,
                  mimeType: file.type,
                  ...itemContent,
                  analysis,
                  searchText
                } 
              })
              .eq('id', createdItem.id);
          }
        } else if (isVideo) {
          // Handle video upload with thumbnail generation
          setUploadState(prev => prev ? { ...prev, stage: 'uploading', stageText: 'Generating thumbnail' } : prev);
          
          let thumbnailUrl: string | null = null;
          let thumbnailStoragePath: string | null = null;
          let duration = 0;
          
          try {
            // Get video metadata
            const metadata = await getVideoMetadata(file);
            duration = metadata.duration;
            
            // Calculate display size for video
            const displaySize = calculateVideoDisplaySize(metadata.width, metadata.height);
            width = displaySize.width;
            height = displaySize.height;
            itemContent = {
              naturalWidth: metadata.width,
              naturalHeight: metadata.height,
              aspectRatio: displaySize.aspectRatio,
            };
            
            // Generate thumbnail
            const thumbnailBlob = await generateVideoThumbnail(file);
            const thumbnailFile = new File([thumbnailBlob], `${file.name}-thumbnail.jpg`, { type: 'image/jpeg' });
            
            // Upload thumbnail
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const thumbnailPath = `${user.id}/${Date.now()}-thumbnail.jpg`;
              const { error: thumbUploadError } = await supabase.storage
                .from('uploads')
                .upload(thumbnailPath, thumbnailFile);
              
              if (!thumbUploadError) {
                thumbnailStoragePath = thumbnailPath;
                const { data: signedUrl } = await supabase.storage
                  .from('uploads')
                  .createSignedUrl(thumbnailPath, 31536000);
                
                if (signedUrl) {
                  thumbnailUrl = signedUrl.signedUrl;
                }
              }
            }
          } catch (e) {
            console.warn('Could not generate video thumbnail:', e);
            // Use defaults
            width = 320;
            height = 180;
          }
          
          // Analyze video with AI
          setUploadState(prev => prev ? { ...prev, stage: 'analyzing', stageText: 'Analyzing video' } : prev);
          
          // Use the analyze-video function for video analysis
          const analysisPromise = supabase.functions.invoke('analyze-video', {
            body: { videoUrl: result.url, thumbnailUrl }
          }).then(({ data }) => data?.analysis || null).catch(() => null);
          
          // Create video item first
          const createdItem = await createItem({
            board_id: targetBoardId,
            item_type: 'video',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            width,
            height,
            content: { 
              videoUrl: result.url, 
              thumbnailUrl,
              filename: result.filename,
              duration,
              mimeType: result.mimeType,
              size: result.size,
              storagePath: result.storagePath,
              thumbnailStoragePath,
              ...itemContent 
            },
          });
          
          // Update with AI analysis when ready
          const analysis = await analysisPromise;
          if (analysis && createdItem) {
            const searchText = `${result.filename} ${analysis}`.toLowerCase();
            await supabase
              .from('items')
              .update({
                content: {
                  videoUrl: result.url,
                  thumbnailUrl,
                  filename: result.filename,
                  duration,
                  mimeType: result.mimeType,
                  size: result.size,
                  storagePath: result.storagePath,
                  thumbnailStoragePath,
                  aiDescription: analysis,
                  searchText,
                  ...itemContent
                }
              })
              .eq('id', createdItem.id);
          }
        } else {
          // Non-image, non-video file
          // PDFs get a larger card for better preview
          const isPdf = result.mimeType === 'application/pdf';
          const fileWidth = isPdf ? 280 : 280;
          const fileHeight = isPdf ? 340 : 100;

          const createdFile = await createItem({
            board_id: targetBoardId,
            item_type: 'file',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            width: fileWidth,
            height: fileHeight,
            content: { url: result.url, filename: result.filename, mimeType: result.mimeType, size: result.size, storagePath: result.storagePath },
          });
          // Read the text in the background so the file is searchable and Partnr can answer from it
          if (createdFile && isExtractableFile(result.filename, result.mimeType, result.size)) {
            requestFileExtraction(createdFile.id);
          }
        }

        offsetX += 20;
        offsetY += 20;
      }
    }

    // "Added", then the tray goes, unless another upload has started since
    setUploadState(prev => prev ? { ...prev, completed: files.length, progress: 0 } : prev);
    setShowSuccess(true);
    setTimeout(() => {
      previews.forEach((p) => p.url && URL.revokeObjectURL(p.url));
      if (runId.current !== run) return;
      setShowSuccess(false);
      setUploadState(null);
    }, 1600);

    if (uploadedItems.length > 0) {
      // Success shown in notification bar, no toast needed
      onFilesUploaded?.(uploadedItems);
    }
  }, [currentBoardId, boards, mode, createItem, getDropPosition, onFilesUploaded]);

  // Drops and pastes during an upload wait their turn instead of fighting over the tray
  const queue = useRef<Promise<void>>(Promise.resolve());
  const uploadFiles = useCallback((files: File[], at: { clientX: number; clientY: number }) => {
    queue.current = queue.current.then(() => runUpload(files, at)).catch((err) => console.error('[Upload] failed:', err));
    return queue.current;
  }, [runUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearDragOver();
    void uploadFiles(Array.from(e.dataTransfer.files), e);
  }, [clearDragOver, uploadFiles]);

  // The middle of what the canvas shows, left of the docked Partnr panel
  const rootRef = useRef<HTMLDivElement>(null);
  const visibleCenter = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return { clientX: window.innerWidth / 2, clientY: window.innerHeight / 2 };
    const width = rect.width - (mode === 'canvas' ? partnrInset : 0);
    return { clientX: rect.left + width / 2, clientY: rect.top + rect.height / 2 };
  }, [mode, partnrInset]);

  // Pasting files (a screenshot, a copied image or file) uploads them like a drop. The wall
  // handles its own pastes (useWallPaste), so only the canvas listens
  const uploadRef = useRef({ uploadFiles, visibleCenter });
  useEffect(() => {
    uploadRef.current = { uploadFiles, visibleCenter };
  });
  useEffect(() => {
    if (mode !== 'canvas') return;
    const onPaste = (e: ClipboardEvent) => {
      if (isFocusInTextOrOverlay(document.activeElement)) return;
      const files = Array.from(e.clipboardData?.files ?? []);
      if (!files.length) return;
      e.preventDefault();
      void uploadRef.current.uploadFiles(files, uploadRef.current.visibleCenter());
    };
    const onRequest = (e: Event) => {
      const { files, at } = (e as CustomEvent<UploadRequest>).detail;
      void uploadRef.current.uploadFiles(files, at ?? uploadRef.current.visibleCenter());
    };
    window.addEventListener('paste', onPaste);
    window.addEventListener(UPLOAD_REQUEST_EVENT, onRequest);
    return () => {
      window.removeEventListener('paste', onPaste);
      window.removeEventListener(UPLOAD_REQUEST_EVENT, onRequest);
    };
  }, [mode]);


  return (
    <div
      ref={rootRef}
      className="relative w-full h-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}

      {/* Dragging files in: a veil over the canvas. On the canvas it stops at the docked
          Partnr panel, which takes its own drops (images for the chat) */}
      <AnimatePresence>
        {isDragOver && <DropVeil key="veil" count={dragCount} mode={mode} inset={mode === 'canvas' ? partnrInset : 0} />}
      </AnimatePresence>

      {/* Uploading, and a moment after: the tray in the corner */}
      <AnimatePresence>
        {uploadState && <UploadTray key="tray" state={uploadState} done={showSuccess} mode={mode} inset={mode === 'canvas' ? partnrInset : 0} />}
      </AnimatePresence>
    </div>
  );
}
